import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined"
import Badge from "@mui/material/Badge"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import CssBaseline from "@mui/material/CssBaseline"
import Tab from "@mui/material/Tab"
import Tabs from "@mui/material/Tabs"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../common/axios"
import Column from "../../common/components/Column"
import GlobalSnackbar from "../../common/components/GlobalSnackbar"
import Row from "../../common/components/Row"
import ConciergeBuyCallingNumberCard from "../customer/settings/ConciergeBuyCallingNumberCard"
import SmsInboxPanel from "../customer/settings/SmsInboxPanel"
import { isSmsOnly } from "../users/accountRole"
import {
  removeToken,
  retrieveUserProfile,
  selectFullName,
  selectProfile,
  selectProfileLoaded,
  selectToken,
} from "../users/reducer"
import PodDialerPanel from "./PodDialerPanel"
import PodInboxPanel from "./PodInboxPanel"
import PodVoicemailsPanel from "./PodVoicemailsPanel"

const AGENT_POLL_MS = 15000

/** The whole app for a texting-only employee: their SMS inbox and nothing else. */
export default function TextsPage() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const token = useSelector(selectToken)
  const profile = useSelector(selectProfile)
  const profileLoaded = useSelector(selectProfileLoaded)
  const fullName = useSelector(selectFullName)
  const axios = useAuthorizedAxiosClient()
  const [agent, setAgent] = useState(null)
  const isPodAgent = Boolean(agent)
  const [tab, setTab] = useState("dialer")

  useEffect(() => {
    if (token) {
      dispatch(retrieveUserProfile())
    }
  }, [dispatch, token])

  useEffect(() => {
    if (token === null) {
      navigate("/user/log-in?next=/texts", { replace: true })
    }
  }, [navigate, token])

  // Pod agents get the dialer and voicemails; everyone else here only texts.
  const loadAgent = useCallback(
    () =>
      axios
        .get("/pods/api/agent/me/")
        .then(({ data }) => setAgent(data?.pod ? data : null))
        .catch(() => setAgent(null)),
    [axios]
  )

  const podDialerOn = Boolean(profile?.features?.pod_dialer)

  useEffect(() => {
    if (!profileLoaded || !isSmsOnly(profile)) return undefined
    if (!podDialerOn) {
      setAgent(null)
      return undefined
    }
    loadAgent()
    const timer = window.setInterval(loadAgent, AGENT_POLL_MS)
    return () => window.clearInterval(timer)
  }, [loadAgent, podDialerOn, profile, profileLoaded])

  useEffect(() => {
    if (profileLoaded && !isSmsOnly(profile)) {
      navigate("/", { replace: true })
    }
  }, [navigate, profile, profileLoaded])

  if (!profileLoaded || !isSmsOnly(profile)) {
    return (
      <Row
        sx={{
          minHeight: "100vh",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CssBaseline />
        <CircularProgress />
      </Row>
    )
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <CssBaseline />
      <Row
        component="header"
        sx={{
          px: { xs: 2, md: 4 },
          py: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: "1px solid",
          borderColor: "colors.gray200",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Texts
        </Typography>
        <Row sx={{ alignItems: "center", gap: 1.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: "colors.gray600",
              display: { xs: "none", sm: "block" },
            }}
          >
            {fullName}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LogoutOutlinedIcon />}
            onClick={() => dispatch(removeToken())}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Log out
          </Button>
        </Row>
      </Row>
      <Column sx={{ px: { xs: 2, md: 4 }, pb: 2 }}>
        {isPodAgent ? (
          <>
            <Tabs value={tab} onChange={(_, value) => setTab(value)}>
              <Tab value="dialer" label="Dialer" />
              <Tab value="pod_texts" label="Pod texts" />
              <Tab
                value="voicemails"
                label={
                  <Badge
                    color="error"
                    badgeContent={agent.unread_voicemails || 0}
                    sx={{ pr: agent.unread_voicemails ? 1.5 : 0 }}
                  >
                    Voicemails
                  </Badge>
                }
              />
              {profile.twilio_voice_phone ? (
                <Tab value="my_texts" label="My number" />
              ) : null}
            </Tabs>
            {tab === "dialer" ? <PodDialerPanel /> : null}
            {tab === "pod_texts" ? <PodInboxPanel /> : null}
            {tab === "voicemails" ? (
              <PodVoicemailsPanel
                dialerConnected={(agent.me?.state || "offline") !== "offline"}
                onChanged={loadAgent}
              />
            ) : null}
            {tab === "my_texts" ? (
              <SmsInboxPanel channel="sms" textingOnly />
            ) : null}
          </>
        ) : profile.twilio_voice_phone ? (
          <SmsInboxPanel channel="sms" textingOnly />
        ) : (
          <Column sx={{ pt: 3, maxWidth: 720, width: "100%", mx: "auto" }}>
            <ConciergeBuyCallingNumberCard textingOnly />
          </Column>
        )}
      </Column>
      <GlobalSnackbar />
    </Box>
  )
}
